/**
 * Avtalearkets vilkår (C3, sak 274). Ett skjema for bud, motbud, godtatt versjon og kontrakt.
 *
 * Nøklene følger rekkefølgen i kontraktens punkt 4.2, så kontrakten (C-kontrakt) kan nummerere dem
 * ved å gå gjennom `DEAL_TERM_KEYS`. ALLE FELT ER VALGFRIE: tomt betyr «ikke avtalt», og det er
 * nettopp det 274.2 bygger på (et tomt felt gir ikke avviksvarsel).
 *
 * Det samme skjemaet dekker leie av spillested (274.8 b). Leievilkårene står i `rental`, resten
 * (dato, rom/scene, tidspunkt, kapasitet) er felles med booking.
 */
import { z } from 'zod';

import {
  exclusivityModeSchema,
  MAX_CONTRACT_TERMS_TEXT,
  MAX_GUEST_LIST_SEATS,
  MAX_SET_LENGTH_MINUTES,
  MAX_TERM_TEXT,
  provisionPartySchema,
} from '../contracts/booking-terms.js';
import { MAX_COST_AMOUNT_MINOR } from '../contracts/economy.js';

const text = z.string().trim().max(MAX_TERM_TEXT);
const longText = z.string().trim().max(MAX_CONTRACT_TERMS_TEXT);
const name = z.string().trim().max(200);
const money = z.number().int().min(0).max(MAX_COST_AMOUNT_MINOR);
const currency = z
  .string()
  .trim()
  .length(3)
  .regex(/^[A-Z]{3}$/);
const percent = z.number().min(0).max(100);
/** `HH:MM`. Et avtaleark er ikke en kjøreplan, så ingen sekunder. */
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Honorarets form. `guarantee` er den eneste webskjemaet tilbyr nå; resten er forberedt (C3). */
export const DEAL_FEE_TYPES = ['guarantee', 'versus', 'door_split', 'rent'] as const;
export const dealFeeTypeSchema = z.enum(DEAL_FEE_TYPES).meta({ id: 'DealFeeType' });
export type DealFeeType = (typeof DEAL_FEE_TYPES)[number];

/**
 * VERSUS (QA15 B7): andelen regnes av netto (billettinntekt etter avtalte kostnader) eller brutto,
 * og avtalen er enten «det høyeste av» garantien og andelen, eller «garanti pluss andel over
 * break-even».
 */
export const DEAL_VERSUS_BASES = ['net', 'gross'] as const;
export type DealVersusBasis = (typeof DEAL_VERSUS_BASES)[number];
export const DEAL_VERSUS_MODES = ['higher_of', 'guarantee_plus'] as const;
export type DealVersusMode = (typeof DEAL_VERSUS_MODES)[number];

/** Om beløpet er med eller uten merverdiavgift, eller fritatt (kulturunntaket). */
export const DEAL_VAT_MODES = ['excl', 'incl', 'exempt'] as const;
export const dealVatModeSchema = z.enum(DEAL_VAT_MODES).meta({ id: 'DealVatMode' });
export type DealVatMode = (typeof DEAL_VAT_MODES)[number];

const provision = z
  .object({ party: provisionPartySchema.optional(), note: text.optional() })
  .meta({ id: 'DealProvision' });

export const dealPaymentSchema = z
  .object({
    /** Andel av honoraret i hele prosent. Andel, ikke beløp: endres honoraret, holder «50 %». */
    pct: z.number().int().min(1).max(100),
    dueOn: z.iso.date(),
    note: z.string().trim().max(200).optional(),
  })
  .meta({ id: 'DealPayment' });
export type DealPayment = z.infer<typeof dealPaymentSchema>;

export const dealTermsSchema = z
  .object({
    /** Artist og format. Id-ene er avsenderens; navnene er det motparten leser. */
    artist: z
      .object({
        bookingArtistId: z.uuid().optional(),
        name: name.optional(),
        formatId: z.uuid().optional(),
        formatName: name.optional(),
      })
      .optional(),
    date: z.iso.date().optional(),
    venue: z
      .object({
        name: name.optional(),
        roomId: z.uuid().optional(),
        roomName: name.optional(),
        city: name.optional(),
      })
      .optional(),
    stage: z.object({ id: z.uuid().optional(), name: name.optional() }).optional(),
    /** `toBeAgreed` er «avtales nærmere»: tidspunktet er bevisst åpent, ikke glemt. */
    time: z
      .object({
        startsAt: clock.optional(),
        toBeAgreed: z.boolean().optional(),
        doorsAt: clock.optional(),
        curfewAt: clock.optional(),
      })
      .optional(),
    setLength: z
      .object({
        minutes: z.number().int().positive().max(MAX_SET_LENGTH_MINUTES).optional(),
        count: z.number().int().min(1).max(10).optional(),
      })
      .optional(),
    billing: text.optional(),
    capacity: z.number().int().nonnegative().optional(),
    ageLimit: name.optional(),
    fee: z
      .object({
        amountMinor: money.optional(),
        currency: currency.optional(),
        vat: dealVatModeSchema.optional(),
        dealType: dealFeeTypeSchema.optional(),
        /** For versus og dørsplitt. */
        artistPercent: percent.optional(),
        afterCosts: z.boolean().optional(),
        /** QA15 B7, versus: netto eller brutto, og hvordan garantien og andelen møtes. */
        versusBasis: z.enum(DEAL_VERSUS_BASES).optional(),
        versusMode: z.enum(DEAL_VERSUS_MODES).optional(),
        /** QA15 B7, leie: hva leien inkluderer, og om billettinntekten går til leietakeren. */
        rentIncludes: text.optional(),
        ticketIncomeToTenant: z.boolean().optional(),
        /**
         * Leiepakken beløpet kom fra (pakke A, `venue_room_rates`). Et øyeblikksbilde: navnet og
         * beløpet står i versjonen, så en senere endret pakke endrer ikke en sendt avtale.
         */
        rentPackage: z.object({ id: z.uuid().optional(), name: name.optional() }).optional(),
      })
      .optional(),
    /**
     * GRUNNLAGET FOR UTREGNINGEN (QA15 B7, spillested og konsertarrangør): billettprisene,
     * kapasiteten (`capacity` over) og de avtalte kostnadene. Tvitvi regner break-even og hva
     * innslaget får ved ulik salgsgrad av dette (`dealOutcome`).
     */
    economics: z
      .object({
        ticketPrices: z
          .array(
            z.object({
              label: name.optional(),
              priceMinor: money,
              /** Antall billetter i denne prisen. Uten antall deles kapasiteten likt. */
              count: z.number().int().nonnegative().optional(),
            }),
          )
          .max(8)
          .optional(),
        agreedCostsMinor: money.optional(),
        costsNote: text.optional(),
      })
      .optional(),
    paymentSchedule: z.array(dealPaymentSchema).max(12).optional(),
    /** Tillegg og buyout utover honoraret. */
    extras: z.object({ amountMinor: money.optional(), note: text.optional() }).optional(),
    accommodation: provision.optional(),
    catering: provision.optional(),
    travel: provision.optional(),
    localTransport: provision.optional(),
    /** Reisefølget i kontrakten er antall og beskrivelse, ikke personer (275). */
    entourage: z
      .object({ count: z.number().int().min(0).max(500).optional(), description: text.optional() })
      .optional(),
    backline: provision.optional(),
    production: provision.optional(),
    guestList: z
      .object({
        seats: z.number().int().min(0).max(MAX_GUEST_LIST_SEATS).optional(),
        note: text.optional(),
      })
      .optional(),
    exclusivity: z
      .object({
        mode: exclusivityModeSchema.optional(),
        radiusKm: z.number().int().min(0).max(10_000).optional(),
        /** Området i ord, når modusen er `text` («Salten»). */
        area: text.optional(),
        from: z.iso.date().optional(),
        to: z.iso.date().optional(),
      })
      /* Programposten krever fra ≤ til (`ck_performance_contracts__exclusivity_order`); et snudd tidsrom
         veltet før projeksjonen i signeringen. ISO-datoer sammenlignes riktig som tekst. */
      .refine((e) => e.from === undefined || e.to === undefined || e.from <= e.to, {
        error: 'exclusivity_order',
        path: ['to'],
      })
      .optional(),
    announcement: text.optional(),
    merch: text.optional(),
    attachments: text.optional(),
    deviations: longText.optional(),
    specialAgreements: longText.optional(),
    /** Standardvilkåret fra organisasjonens vilkårsliste, med teksten slik den var da den ble valgt. */
    standardTerms: z
      .object({
        id: z.uuid().optional(),
        name: name.optional(),
        version: z.string().trim().max(40).optional(),
        /* Vilkårslistas tekst kan være lang (20 000 tegn i Innstillinger). */
        text: z.string().trim().max(20000).optional(),
      })
      .optional(),
    /** Leie av spillested (274.8 b). Feltene `date_requests` og `concert_deals` har i dag. */
    rental: z
      .object({
        rentMinor: money.optional(),
        currency: currency.optional(),
        terms: longText.optional(),
        layoutName: name.optional(),
        expectedAudience: z.number().int().nonnegative().optional(),
        /** Billettfordelingen i ord («80/20 etter kostnader»). Tallene står i `fee` ved splitt. */
        ticketSplit: text.optional(),
        merchPercent: percent.optional(),
        holdUntil: z.iso.date().optional(),
      })
      .optional(),
  })
  .meta({ id: 'DealSheetTerms' });
export type DealSheetTerms = z.infer<typeof dealTermsSchema>;

/**
 * BETALINGSPLANEN SUMMERER TIL 100 (AI-ANALYSE §8.1 #1). Sto bare i nettsiden (`paymentPlanProblem`)
 * for avtalearket; budets plan sjekkes også i tjenesten (`writePayments`) med den samme koden.
 * En tom plan er lovlig: «ingen plan avtalt».
 */
export const PAYMENT_PLAN_SUM = 'payment_plan_sum';
export function paymentPlanTotal(rows: readonly { readonly pct: number }[]): number {
  return rows.reduce((sum, row) => sum + row.pct, 0);
}

/**
 * Vilkårene slik de SKRIVES (motbud, endring, kontraktutkast, budets avtaleark). Regelen står her og
 * ikke på `dealTermsSchema`, som også leser lagrede versjoner: en gammel versjon som ikke summerer,
 * skal fortsatt kunne vises.
 */
export const dealTermsInputSchema = dealTermsSchema
  .superRefine((terms, ctx) => {
    const rows = terms.paymentSchedule ?? [];
    const total = paymentPlanTotal(rows);
    if (rows.length > 0 && total !== 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['paymentSchedule'],
        message: 'The payment plan adds up to ' + String(total) + ' %, not 100.',
        params: { code: PAYMENT_PLAN_SUM },
      });
    }
  })
  .meta({ id: 'DealSheetTermsInput' });

/** Toppnøklene i kontraktens rekkefølge. Diffen og kontrakten går gjennom dem i denne rekkefølgen. */
export const DEAL_TERM_KEYS = [
  'artist',
  'date',
  'venue',
  'stage',
  'time',
  'setLength',
  'billing',
  'capacity',
  'ageLimit',
  'fee',
  'paymentSchedule',
  'extras',
  'accommodation',
  'catering',
  'travel',
  'localTransport',
  'entourage',
  'backline',
  'production',
  'guestList',
  'exclusivity',
  'announcement',
  'merch',
  'attachments',
  'deviations',
  'specialAgreements',
  'standardTerms',
  'rental',
  /* QA15 B7: sist, så kontraktens nummererte punkter ikke flytter seg (grunnlaget står under honoraret). */
  'economics',
] as const satisfies readonly (keyof DealSheetTerms)[];
export type DealTermKey = (typeof DEAL_TERM_KEYS)[number];

/** Feltene 274.2 låser når kontrakten er signert. Scene og tidspunkt er «avtalt», ikke låst. */
export const DEAL_LOCKED_KEYS = [
  'fee',
  'paymentSchedule',
  'extras',
  'date',
  'setLength',
  'accommodation',
  'catering',
  'travel',
  'localTransport',
  'backline',
  'production',
  'exclusivity',
  'guestList',
] as const satisfies readonly DealTermKey[];

export interface DealTermsChange {
  /** Toppnøkkelen, til å gruppere og oversette (`deal.terms.<key>`). */
  key: DealTermKey;
  /** Hele stien til bladet, f.eks. `fee.amountMinor`. Lister sammenlignes som én verdi. */
  path: string;
  before: unknown;
  after: unknown;
}

/** Tomt er tomt: `undefined`, `null`, `''`, `[]` og et objekt uten innhold betyr «ikke avtalt». */
function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).every(isEmpty);
  }
  return false;
}

function normalize(value: unknown): unknown {
  if (isEmpty(value)) return undefined;
  if (Array.isArray(value)) return value.map(normalize);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value).sort()) {
      const v = normalize((value as Record<string, unknown>)[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return typeof value === 'string' ? value.trim() : value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function walk(
  key: DealTermKey,
  path: string,
  a: unknown,
  b: unknown,
  out: DealTermsChange[],
): void {
  if (isPlainObject(a) || isPlainObject(b)) {
    const left = isPlainObject(a) ? a : {};
    const right = isPlainObject(b) ? b : {};
    const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();
    for (const k of keys) walk(key, `${path}.${k}`, left[k], right[k], out);
    return;
  }
  if (JSON.stringify(a) !== JSON.stringify(b)) out.push({ key, path, before: a, after: b });
}

/**
 * Hva som er endret fra `a` (forrige versjon) til `b` (den nye), blad for blad.
 *
 * Tomme verdier normaliseres først, så et felt som går fra `''` til fraværende ikke er en endring.
 * `a` kan være `null` for første versjon: da er alt som er fylt ut, en endring.
 */
export function diffDealTerms(
  a: DealSheetTerms | null | undefined,
  b: DealSheetTerms,
): DealTermsChange[] {
  const left = (normalize(a ?? {}) ?? {}) as Record<string, unknown>;
  const right = (normalize(b) ?? {}) as Record<string, unknown>;
  const out: DealTermsChange[] = [];
  for (const key of DEAL_TERM_KEYS) walk(key, key, left[key], right[key], out);
  return out;
}

/** Toppnøklene som er endret, i kontraktens rekkefølge. Det chatkortet viser. */
export function changedDealTermKeys(
  a: DealSheetTerms | null | undefined,
  b: DealSheetTerms,
): DealTermKey[] {
  const changed = new Set(diffDealTerms(a, b).map((c) => c.key));
  return DEAL_TERM_KEYS.filter((k) => changed.has(k));
}

/**
 * Vilkårene uten tomme verdier: tomme tekster, tomme lister og objekter uten innhold tas bort, og
 * tekst trimmes. Det som lagres i en versjon, er det som faktisk er avtalt.
 */
export function compactDealTerms(terms: DealSheetTerms): DealSheetTerms {
  return (normalize(terms) ?? {}) as DealSheetTerms;
}
